// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { SafeClientTypes } from '@safecoin/safe/client';
import { SafeJsonRpcClient, JsonRpcHTTPTransport, getJsonRpcFullnodeUrl } from '@safecoin/safe/jsonRpc';

export enum Network {
	LOCAL = 'LOCAL',
	DEVNET = 'DEVNET',
	TESTNET = 'TESTNET',
	MAINNET = 'MAINNET',
}

export const NetworkConfigs: Record<Network, { url: string; network: SafeClientTypes.Network }> = {
  [Network.LOCAL]: { url: import.meta.env.VITE_RPC_URL || "https://api.safecoin.dev", network: "localnet" },
  [Network.DEVNET]: { url: import.meta.env.VITE_DEVNET_RPC_URL || getJsonRpcFullnodeUrl("devnet"), network: "devnet" },
  [Network.TESTNET]: { url: import.meta.env.VITE_TESTNET_RPC_URL || getJsonRpcFullnodeUrl("testnet"), network: "testnet" },
  [Network.MAINNET]: { url: import.meta.env.VITE_MAINNET_RPC_URL || getJsonRpcFullnodeUrl("mainnet"), network: "mainnet" },
};

const defaultClientMap: Map<Network | string, SafeJsonRpcClient> = new Map();

// NOTE: This class should not be used directly in React components, prefer to use the useSafeClient() hook instead
export const createSafeClient = (network: Network | string) => {
	const existingClient = defaultClientMap.get(network);
	if (existingClient) return existingClient;

	// Anything that is not a known network is a custom RPC URL, treated as a local network.
	const { url, network: clientNetwork } =
		network in Network ? NetworkConfigs[network as Network] : { url: network, network: 'localnet' };

	const client = new SafeJsonRpcClient({
    transport: new JsonRpcHTTPTransport({ url }),
    network: clientNetwork,
  });
	defaultClientMap.set(network, client);
	return client;
};
